import Vue from "vue";
import Router, { Route } from "vue-router";
import VueBodyClass from "vue-body-class";
import i18n from "./i18n";
import { getPartnerCustomization, isCustomisationEnabled } from "@/views/shared/partners";

import LoginView from "./views/auth/LoginView.vue";
import DeleteAccountView from "./views/auth/DeleteAccountView.vue";
import DiscourseView from "./views/auth/DiscourseView.vue";
import ResumeView from "./views/auth/ResumeView.vue";
import CreateAccountView from "./views/auth/CreateAccountView.vue";
import RecoverAccountView from "./views/auth/RecoverAccountView.vue";
import ResetPasswordView from "./views/auth/ResetPasswordView.vue";
import UserView from "./views/auth/UserView.vue";

import ProjectsView from "./views/projects/ProjectsView.vue";
import ProjectEditView from "./views/projects/ProjectEditView.vue";
import ProjectUpdateEditView from "./views/projects/ProjectUpdateEditView.vue";
import ProjectView from "./views/projects/ProjectView.vue";
import ProjectBigMap from "./views/projects/ProjectBigMap.vue";

import FeaturesView from "./views/FeaturesView.vue";

import StationsView from "./views/StationsView.vue";
import ExploreView from "./views/viz/ExploreView.vue";

import NotificationsView from "./views/notifications/NotificationsView.vue";

import AdminMain from "./views/admin/AdminMain.vue";
import AdminUsers from "./views/admin/AdminUsers.vue";
import AdminStations from "./views/admin/AdminStations.vue";
import AdminModeration from "./views/admin/AdminModeration.vue";

import ImportDataView from "./views/import/ImportView.vue";

import StationView from "./views/station/StationView.vue";
import NotFoundView from "./views/NotFoundView.vue";

import { deserializeBookmark } from "./views/viz/viz";
import TermsView from "@/views/auth/TermsView.vue";
import StationPhotosView from "@/views/station/StationPhotosView.vue";
import ExploreModuleLoader from "@/views/viz/ExploreModuleLoader.vue";
import { MapViewType } from "@/api/api";
import { ActionTypes, UserState } from "@/store";

let vueRouter: Router;

Vue.use(Router);

function makeDefaultRouteForProject(projectId: number) {
    return {
        path: "/",
        name: "root",
        component: ProjectBigMap,
        props: (_route) => {
            return {
                id: projectId,
                forcePublic: false,
                bigMap: true,
            };
        },
        meta: {
            secured: false,
            viewType: MapViewType.map,
        },
    };
}

function getRoot() {
    const partnerCustomization = getPartnerCustomization();
    if (partnerCustomization == null || partnerCustomization.projectId == null) {
        return {
            path: "/",
            name: "root",
            component: ProjectsView,
            meta: {
                secured: false,
            },
        };
    }
    return makeDefaultRouteForProject(partnerCustomization.projectId);
}

function isAdminRoute(route: Route): boolean {
    return route.matched.some((record) => record.meta.admin);
}

const routes = [
    {
        path: "/login",
        name: "login",
        component: LoginView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
        props: true,
    },
    {
        path: "/login/discourse",
        name: "discourseLogin",
        component: DiscourseView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/login/keycloak",
        name: "loginKeycloak",
        component: ResumeView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/login/:token",
        name: "loginResume",
        component: ResumeView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/spoof",
        name: "spoof",
        component: LoginView,
        props: (_route) => {
            return {
                spoofing: true,
            };
        },
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/register",
        name: "register",
        component: CreateAccountView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/delete-account",
        name: "deleteAccount",
        component: DeleteAccountView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/recover",
        name: "recover",
        component: RecoverAccountView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/recover/complete",
        name: "reset",
        component: ResetPasswordView,
        meta: {
            bodyClass: "blue-background",
            secured: false,
        },
    },
    {
        path: "/internal/features",
        name: "features",
        component: FeaturesView,
        meta: {
            secured: true,
        },
    },
    {
        path: "/terms",
        name: "terms",
        component: TermsView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/projects/invitation",
        name: "viewInvites",
        component: ProjectsView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/user",
        name: "user",
        component: UserView,
        meta: {
            secured: true,
        },
    },
    {
        path: "/dashboard/user",
        name: "editUser",
        component: UserView,
        meta: {
            secured: true,
        },
    },
    {
        path: "/dashboard",
        name: "projects",
        component: ProjectsView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:id",
        name: "viewProject",
        component: ProjectView,
        props: (route) => {
            return {
                id: Number(route.params.id),
                forcePublic: false,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:id/activity",
        name: "viewProjectActivity",
        component: ProjectView,
        props: (route) => {
            return {
                id: Number(route.params.id),
                forcePublic: false,
                activityVisible: true,
            };
        },
        meta: {
            bodyClass: "disable-scrolling",
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:id/public",
        name: "viewProjectPublic",
        component: ProjectView,
        props: (route) => {
            return {
                id: Number(route.params.id),
                forcePublic: true,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:id/map",
        name: "viewProjectBigMap",
        component: ProjectBigMap,
        props: (route) => {
            return {
                id: Number(route.params.id),
                forcePublic: false,
                bigMap: true,
            };
        },
        meta: {
            secured: false,
            viewType: MapViewType.map,
        },
    },
    {
        path: "/dashboard/projects/:id/list",
        name: "viewProjectBigMapList",
        component: ProjectBigMap,
        props: (route) => {
            return {
                id: Number(route.params.id),
                forcePublic: false,
                bigMap: true,
            };
        },
        meta: {
            secured: false,
            viewType: MapViewType.list,
        },
    },
    {
        path: "/dashboard/project/add",
        name: "addProject",
        component: ProjectEditView,
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:id/edit",
        name: "editProject",
        component: ProjectEditView,
        props: (route) => {
            return {
                id: Number(route.params.id),
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/project-update/add",
        name: "addProjectUpdate",
        component: ProjectUpdateEditView,
        props: true,
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/project-updates/:id/edit",
        name: "editProjectUpdate",
        component: ProjectUpdateEditView,
        props: (route) => {
            return {
                id: Number(route.params.id),
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/stations/map",
        name: "mapAllStations",
        component: StationsView,
        props: true,
        meta: {
            bodyClass: "map-view",
            secured: true,
            viewType: MapViewType.map,
        },
    },
    {
        path: "/dashboard/stations/list",
        name: "listAllStations",
        component: StationsView,
        props: true,
        meta: {
            bodyClass: "map-view",
            secured: true,
            viewType: MapViewType.list,
        },
    },
    {
        path: "/dashboard/stations/:id(\\d+)",
        name: "mapStation",
        component: StationsView,
        props: (route) => {
            return {
                id: Number(route.params.id),
            };
        },
        meta: {
            bodyClass: "map-view",
            secured: false,
        },
    },
    {
        path: "/dashboard/stations/all/:bounds",
        name: "mapAllStationsBounds",
        component: StationsView,
        props: (route) => {
            return {
                bounds: JSON.parse(route.params.bounds),
            };
        },
        meta: {
            bodyClass: "map-view",
            secured: false,
        },
    },
    {
        path: "/dashboard/stations/:bounds/:id(\\d+)",
        name: "mapStationBounds",
        component: StationsView,
        props: (route) => {
            return {
                id: Number(route.params.id),
                bounds: JSON.parse(route.params.bounds),
            };
        },
        meta: {
            bodyClass: "map-view",
            secured: false,
        },
    },
    {
        path: "/station/:stationId",
        name: "viewStationFromMap",
        component: StationView,
        props: (route) => {
            return {
                stationId: Number(route.params.stationId),
                forcePublic: false,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/projects/:projectId/station/:stationId",
        name: "viewStation",
        component: StationView,
        props: (route) => {
            return {
                stationId: Number(route.params.stationId),
                projectId: Number(route.params.projectId),
                forcePublic: false,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/station/:stationId/photos",
        name: "viewStationPhotos",
        component: StationPhotosView,
        props: (route) => {
            return {
                stationId: Number(route.params.stationId),
                forcePublic: false,
            };
        },
        meta: {
            secured: false,
            bodyClass: "disable-scrolling",
        },
    },
    {
        path: "/dashboard/projects/:projectId/station/:stationId/photos",
        name: "viewProjectStationPhotos",
        component: StationPhotosView,
        props: (route) => {
            return {
                stationId: Number(route.params.stationId),
                projectId: Number(route.params.projectId),
                forcePublic: false,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/dashboard/explore",
        name: "exploreBookmark",
        component: ExploreView,
        props: (route) => {
            if (route.query.bookmark) {
                return {
                    bookmark: deserializeBookmark(route.query.bookmark),
                };
            }
            return {};
        },
        meta: {},
    },
    {
        path: "/viz",
        name: "exploreShortBookmark",
        component: ExploreView,
        props: (route) => {
            if (route.query.v) {
                return {
                    token: route.query.v,
                };
            }
            return {};
        },
        meta: {},
    },
    {
        path: "/dashboard/explore/:bookmark",
        name: "exportBookmarkOld",
        component: ExploreView,
        props: (route) => {
            return {
                bookmark: deserializeBookmark(route.params.bookmark),
            };
        },
        meta: {},
    },
    {
        path: "/viz/export",
        name: "exportWorkspace",
        component: ExploreView,
        props: (route) => {
            return {
                token: route.query.v,
                exportsVisible: true,
            };
        },
        meta: {
            bodyClass: "disable-scrolling",
            secured: true,
        },
    },
    {
        path: "/viz/share",
        name: "shareWorkspace",
        component: ExploreView,
        props: (route) => {
            return {
                token: route.query.v,
                shareVisible: true,
            };
        },
        meta: {
            bodyClass: "disable-scrolling",
            secured: false,
        },
    },
    {
        path: "/viz/module/:stationId/:moduleKey",
        name: "exploreModule",
        component: ExploreModuleLoader,
        props: (route) => {
            return {
                stationId: Number(route.params.stationId),
                moduleKey: route.params.moduleKey,
            };
        },
        meta: {
            secured: false,
        },
    },
    {
        path: "/notifications",
        name: "notifications",
        component: NotificationsView,
        meta: {
            secured: true,
        },
    },
    {
        path: "/admin",
        name: "adminMain",
        component: AdminMain,
        props: (_route) => {
            return {};
        },
        meta: {
            admin: true,
            secured: true,
        },
    },
    {
        path: "/admin/users",
        name: "adminUsers",
        component: AdminUsers,
        props: (route) => {
            const page = route.query.page ? parseInt(route.query.page as string) : 0;
            const user = route.query.user ? parseInt(route.query.user as string) : null;
            return { page, user };
        },
        meta: {
            admin: true,
            secured: true,
        },
    },
    {
        path: "/admin/stations",
        name: "adminStations",
        component: AdminStations,
        props: (route) => {
            const page = route.query.page ? parseInt(route.query.page) : 0;
            const station = route.query.station ? parseInt(route.query.station) : null;
            return {
                page: page,
                station: station,
            };
        },
        meta: {
            admin: true,
            secured: true,
        },
    },
    {
        path: "/admin/moderation",
        name: "adminModeration",
        component: AdminModeration,
        meta: {
            admin: true,
            secured: true,
        },
    },
    {
        path: "/data/import",
        name: "importData",
        component: ImportDataView,
        props: (route) => {
            return {
                stationId: route.query.stationId ? parseInt(route.query.stationId) : null,
            };
        },
        meta: {
            secured: true,
        },
    },
    {
        path: "/data/import/:token",
        name: "importDataStatus",
        component: ImportDataView,
        props: (route) => {
            return {
                token: route.params.token,
            };
        },
        meta: {
            secured: true,
        },
    },
    getRoot(),
    {
        path: "*",
        name: "notFound",
        component: NotFoundView,
        meta: {
            secured: false,
        },
    },
];

export default function routerFactory(store) {
    const router = new Router({
        mode: "history",
        base: process.env.BASE_URL,
        routes: routes,
        scrollBehavior(to, from, _savedPosition) {
            if (to.name == from.name) {
                return null;
            }
            // console.log("scrolling-to-top");
            return { x: 0, y: 0 };
        },
    });

    vueRouter = router;

    const vueBodyClass = new VueBodyClass(routes);
    router.beforeEach((to, from, next) => {
        vueBodyClass.guard(to, next);
    });

    // Global navigation guard to set the page title
    router.afterEach((_to) => {
        updateDocumentTitle();
    });

    router.beforeEach((to, from, next) => {
        console.log("nav", from.name, "->", to.name);

        const getUserState = () => (store.state as { user: UserState }).user;

        const resolve = () => {
            const { isAuthenticated, isTncValid, isAdmin } = store.getters;
            const isInitialNavigation = from.name === null && (to.name === null || to.name === "login");
            const isSecured = to.matched.some((record) => record.meta.secured);

            if (isInitialNavigation) {
                if (!isAuthenticated) {
                    next();
                    return;
                }
                next(!isTncValid && to.name !== "login" ? "/terms" : "/dashboard");
                return;
            }

            if (isSecured) {
                if (!isAuthenticated) {
                    next("/login?" + new URLSearchParams({ after: to.fullPath }).toString());
                    return;
                }
                if (isAdminRoute(to) && !isAdmin) {
                    next("/dashboard");
                } else if (!isTncValid) {
                    next("/terms");
                } else {
                    next();
                }
                return;
            }

            if (to.name === null) {
                next(isAuthenticated ? "/dashboard" : "/login");
            } else {
                next();
            }
        };

        if (store.getters.isAuthenticated && getUserState().user === null) {
            store
                .dispatch(ActionTypes.REFRESH_CURRENT_USER)
                .then(resolve)
                .catch(() => next("/login"));
        } else {
            resolve();
        }
    });

    return router;
}

export function updateDocumentTitle(): void {
    const routeName = vueRouter ? vueRouter.currentRoute.name : null;

    if (routeName) {
        const partnerName = isCustomisationEnabled() ? "FloodNet" : "FieldKit";
        const titleText = i18n.t(`pageTitles.${routeName}`, "", { fallbackWarn: false });
        if (String(titleText) !== `pageTitles.${routeName}`) {
            document.title = `${titleText} - ${partnerName}`;
        } else {
            document.title = partnerName;
        }
    }
}
